// ─────────────────────────────────────────────
// AdminBookings.jsx — responsive mobile/tablet
// ─────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from "react";
import api from "../api/axios";
import AdminLayout from "./AdminLayout";

const ArrowRightIcon = () => <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const ChevLeft       = () => <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const ChevRight      = () => <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const SpinIcon       = () => <div style={{ width:24, height:24, border:"2px solid #2a2d38", borderTopColor:"#4ade80", borderRadius:"50%", animation:"spin 0.6s linear infinite" }} />;

const Avatar = ({ name }) => {
  const c = name?.charAt(0)?.toUpperCase() || "C";
  return (
    <div style={{ width:34, height:34, borderRadius:"50%", background:"rgba(74,222,128,0.12)", color:"#4ade80", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, flexShrink:0 }}>
      {c}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    CONFIRMED:{ bg:"rgba(74,222,128,0.12)", color:"#4ade80",  label:"Confirmé"    },
    PENDING:  { bg:"rgba(251,191,36,0.12)", color:"#fbbf24",  label:"En attente"  },
    CANCELLED:{ bg:"rgba(248,113,113,0.12)",color:"#f87171",  label:"Annulé"      },
  };
  const m = map[status?.toUpperCase()] || map.PENDING;
  return <span style={{ fontSize:11, padding:"3px 9px", borderRadius:20, background:m.bg, color:m.color, fontWeight:600, fontFamily:"'DM Mono',monospace" }}>{m.label}</span>;
};

/* ── Mobile card for a single booking ── */
const BookingCard = ({ b }) => (
  <div className="booking-card">
    <div className="booking-card-top">
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <Avatar name={b.client} />
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:"#e8e9ed" }}>{b.client}</div>
          <div style={{ fontSize:11, color:"#8b8fa8" }}>{b.company}</div>
        </div>
      </div>
      <StatusBadge status={b.status} />
    </div>
    <div className="booking-card-route">
      <span className="city-tag">{b.route?.split("→")[0]?.trim() || b.route}</span>
      <span style={{ color:"#4a4d5a" }}><ArrowRightIcon /></span>
      <span className="city-tag">{b.route?.split("→")[1]?.trim() || ""}</span>
    </div>
    <div className="booking-card-footer">
      <span style={{ fontFamily:"'DM Mono',monospace", color:"#4ade80", fontWeight:700, fontSize:13 }}>
        {Number(b.amount).toLocaleString()} MGA
      </span>
      <span style={{ fontSize:11, color:"#4ade80", fontFamily:"'DM Mono',monospace", padding:"3px 8px", borderRadius:6, background:"rgba(74,222,128,0.1)" }}>
        {new Date(b.date).toLocaleDateString('fr-FR', { year:'numeric', month:'short', day:'numeric' })}
      </span>
    </div>
  </div>
);

export const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoading]  = useState(true);
  const [hasMore,  setHasMore]  = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/bookings?page=${page}`);
      setBookings(res.data);
      setHasMore(res.data.length >= 10);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [page]);

  useEffect(() => { load(); }, [load]);

  if (loading && bookings.length === 0) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", background:"#0f1117", flexDirection:"column", gap:12 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <SpinIcon />
      <p style={{ color:"#8b8fa8", fontSize:13 }}>Chargement des réservations…</p>
    </div>
  );

  const groupedByTransporter = bookings.reduce((acc, booking) => {
    const transporter = booking.transporter || "Sans transporteur";
    if (!acc[transporter]) acc[transporter] = [];
    acc[transporter].push(booking);
    return acc;
  }, {});

  return (
    <AdminLayout title="Réservations" subtitle="Gestion des réservations clients">
      <style>{bookingCSS}</style>

      {bookings.length === 0 && !loading ? (
        <div style={bs.card}>
          <div style={bs.empty}>
            <div style={{ fontSize:32, marginBottom:8 }}>🎫</div>
            <div style={{ fontSize:14, color:"#8b8fa8" }}>Aucune réservation trouvée</div>
          </div>
        </div>
      ) : (
        <>
          {Object.keys(groupedByTransporter).map((transporter) => (
            <div key={transporter} style={{ ...bs.card, marginBottom:16 }}>
              <div style={bs.cardHeader}>
                <div style={bs.cardTitle}>
                  {loading
                    ? <span style={{ fontSize:13, color:"#8b8fa8" }}>Chargement…</span>
                    : <>{transporter} · {groupedByTransporter[transporter].length} réservation{groupedByTransporter[transporter].length !== 1 ? "s" : ""}</>
                  }
                </div>
              </div>

              {/* Desktop table */}
              <div className="table-wrap">
                <table style={bs.table}>
                  <thead>
                    <tr>{["Client","Trajet","Montant","Statut","Reference Paiement","Company","Date"].map(h=>(
                      <th key={h} style={bs.th}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {groupedByTransporter[transporter].map((b, i) => (
                      <tr key={i} style={bs.tr} className="brow">
                        <td style={bs.td}>
                          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <Avatar name={b.client} />
                            <span style={{ fontSize:13, fontWeight:600, color:"#e8e9ed" }}>{b.client}</span>
                          </div>
                        </td>
                        <td style={bs.td}>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <span style={bs.cityTag}>{b.route?.split("→")[0]?.trim() || b.route}</span>
                            <span style={{ color:"#2a2d38" }}><ArrowRightIcon /></span>
                            <span style={bs.cityTag}>{b.route?.split("→")[1]?.trim() || ""}</span>
                          </div>
                        </td>
                        <td style={bs.td}>
                          <span style={{ fontFamily:"'DM Mono',monospace", color:"#4ade80", fontWeight:600, fontSize:13 }}>
                            {Number(b.amount).toLocaleString()} MGA
                          </span>
                        </td>
                        <td style={bs.td}><StatusBadge status={b.status} /></td>
                         <td style={bs.td}>
                          <span style={{ fontSize:13, fontWeight:600, color:"#e8e9ed" }}>{b.reference}</span>
                        </td>
                        <td style={bs.td}>
                          <span style={{ fontSize:13, fontWeight:600, color:"#e8e9ed" }}>{b.company}</span>
                        </td>
                        <td style={bs.td}>
                          <span style={{ fontSize:13, fontWeight:600, color:"#4ade80", fontFamily:"'DM Mono',monospace", padding:"3px 8px", borderRadius:6, background:"rgba(74,222,128,0.1)" }}>
                            {new Date(b.date).toLocaleDateString('fr-FR', { year:'numeric', month:'short', day:'numeric' })}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="cards-wrap">
                {groupedByTransporter[transporter].map((b, i) => (
                  <BookingCard key={i} b={b} />
                ))}
              </div>
            </div>
          ))}

          {/* PAGINATION */}
          <div style={{ ...bs.card, marginBottom:0 }}>
            <div style={bs.pagination}>
              <button
                style={{ ...bs.pageBtn, ...(page===1 ? bs.pageBtnDisabled : {}) }}
                onClick={() => setPage(p => Math.max(1,p-1))}
                disabled={page===1}
              >
                <ChevLeft /> Précédent
              </button>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                {[page-1, page, page+1].filter(p=>p>0).map(p => (
                  <button key={p} style={{ ...bs.pageNum, ...(p===page ? bs.pageNumActive : {}) }} onClick={() => setPage(p)}>
                    {p}
                  </button>
                ))}
              </div>
              <button
                style={{ ...bs.pageBtn, ...(!hasMore ? bs.pageBtnDisabled : {}) }}
                onClick={() => setPage(p => p+1)}
                disabled={!hasMore}
              >
                Suivant <ChevRight />
              </button>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

const bs = {
  card:           { background:"#16181f", border:"0.5px solid #2a2d38", borderRadius:12, overflow:"hidden" },
  cardHeader:     { padding:"14px 20px", borderBottom:"0.5px solid #2a2d38", display:"flex", alignItems:"center", justifyContent:"space-between" },
  cardTitle:      { fontSize:13, color:"#8b8fa8", fontWeight:500 },
  table:          { width:"100%", borderCollapse:"collapse", fontSize:13 },
  th:             { padding:"10px 16px", color:"#8b8fa8", fontSize:11, fontWeight:500, textTransform:"uppercase", letterSpacing:"0.5px", borderBottom:"0.5px solid #2a2d38", textAlign:"left", background:"#13151b" },
  td:             { padding:"12px 16px", borderBottom:"0.5px solid #1e2029", verticalAlign:"middle" },
  tr:             {},
  cityTag:        { fontSize:11, padding:"3px 8px", borderRadius:6, background:"#1e2029", color:"#c8cad8" },
  empty:          { textAlign:"center", padding:"48px 0" },
  pagination:     { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px", borderTop:"0.5px solid #2a2d38" },
  pageBtn:        { display:"flex", alignItems:"center", gap:6, fontSize:12, padding:"7px 14px", background:"none", border:"0.5px solid #2a2d38", borderRadius:8, color:"#8b8fa8", cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" },
  pageBtnDisabled:{ opacity:0.4, cursor:"not-allowed" },
  pageNum:        { width:30, height:30, borderRadius:8, border:"0.5px solid #2a2d38", background:"none", fontSize:12, color:"#8b8fa8", cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center" },
  pageNumActive:  { background:"#4ade80", color:"#0f1117", borderColor:"#4ade80", fontWeight:700 },
};

const bookingCSS = `
  @keyframes spin { to { transform: rotate(360deg); } }
  .brow:hover td { background: #1a1c25 !important; }

  /* Desktop: table visible, cards hidden */
  .table-wrap { overflow-x: auto; }
  .cards-wrap { display: none; }

  /* Mobile / Tablet ≤ 768px */
  @media (max-width: 768px) {
    .table-wrap { display: none; }
    .cards-wrap { display: flex; flex-direction: column; gap: 10px; padding: 12px; }

    .booking-card {
      background: #13151b;
      border: 0.5px solid #2a2d38;
      border-radius: 10px;
      padding: 14px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .booking-card-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }
    .booking-card-route {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
    }
    .booking-card-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 6px;
    }
    .city-tag {
      font-size: 11px;
      padding: 3px 8px;
      border-radius: 6px;
      background: #1e2029;
      color: #c8cad8;
    }
  }
`;

export default AdminBookings;